/**
 * Servico de API - WorkSession (Sessoes de Trabalho)
 * 
 * Este modulo gerencia todas as operacoes relacionadas as sessoes de trabalho:
 * - Controle de timer em tempo real
 * - Gerenciamento de sessoes ativas, pausadas e finalizadas
 * - Calculo de duracao e horas trabalhadas
 * - Integracao com sistema de heartbeat
 * - Helpers para formatacao e validacao
 * 
 * Funcionalidades principais:
 * - Iniciar, pausar, retomar e parar sessoes
 * - Listagem de sessoes ativas e historico
 * - Calculo de duracao em tempo real
 * - Formatacao de tempo para exibicao
 * - Validacao de status de sessoes
 * - Integracao com React Query
 * 
 * @author Equipe GestaoeCRM
 * @date 2025-10-03
 * @version 0.16.6
 */

import api from './api';

// Configurar URL da API - Detecta automaticamente o host correto
const getApiUrl = (): string => {
  if ((import.meta as any).env?.VITE_API_URL) {
    return (import.meta as any).env.VITE_API_URL;
  }
  const currentHost = window.location.hostname;
  return `http://${currentHost}:3001`;
};

const API_URL = getApiUrl();

// ============================================================================
// INTERFACES E TIPOS
// ============================================================================

/**
 * Interface principal para WorkSession (Sessao de Trabalho)
 * 
 * Representa uma sessao de trabalho completa com todas as informacoes
 * necessarias para controle de tempo e gerenciamento.
 * 
 * Propriedades obrigatorias:
 * - id: Identificador unico da sessao
 * - tenantId: ID do tenant (isolamento multi-tenant)
 * - orderOfServiceId: ID da OS vinculada
 * - userId: ID do usuario responsavel
 * - startTime: Timestamp de inicio
 * - duration: Duracao total em horas
 * - isActive: Se a sessao esta ativa
 * - isPaused: Se a sessao esta pausada
 * - totalPausedTime: Tempo total pausado em horas
 * - createdAt/updatedAt: Timestamps de controle
 * 
 * Propriedades opcionais:
 * - endTime: Timestamp de fim (null se ativa)
 * - description: Descricao da sessao
 * - pausedAt/resumedAt: Timestamps de pausa/retomada
 * - orderOfService: Dados da OS vinculada
 * - user: Dados do usuario responsavel
 */
export interface WorkSession {
  /** ID unico da sessao */
  id: string;
  /** ID do tenant (isolamento multi-tenant) */
  tenantId: string;
  /** ID da OS vinculada */
  orderOfServiceId: string;
  /** ID do usuario responsavel */
  userId: string;
  /** Timestamp de inicio da sessao */
  startTime: string;
  /** Timestamp de fim da sessao (null se ativa) */
  endTime: string | null;
  /** Duracao total em horas */
  duration: number;
  /** Descricao da sessao (opcional) */
  description: string | null;
  /** Se a sessao esta ativa */
  isActive: boolean;
  /** Se a sessao esta pausada */
  isPaused: boolean;
  /** Timestamp de pausa (opcional) */
  pausedAt: string | null;
  /** Timestamp de retomada (opcional) */
  resumedAt: string | null;
  /** Tempo total pausado em horas */
  totalPausedTime: number;
  /** Data de criacao */
  createdAt: string;
  /** Data de ultima atualizacao */
  updatedAt: string;
  /** Dados da OS vinculada (opcional) */
  orderOfService?: {
    id: string;
    title: string;
    type: string;
    status: string;
    priority?: string;
    actualHours?: number;
    estimatedHours?: number;
    createdAt?: string;
    updatedAt?: string;
    userId?: string;
    isUrgent?: boolean;
    demand?: {
      id: string;
      title: string;
      project?: {
        id: string;
        name: string;
        client?: {
          id: string;
          name: string;
        };
      };
    };
    user?: {
      id: string;
      name: string;
      email: string;
    };
  };
  /** Dados do usuario responsavel (opcional) */
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

/**
 * Interface para dados de inicio de sessao
 * 
 * Define os dados opcionais para iniciar uma nova sessao de trabalho.
 */
export interface StartSessionRequest {
  /** Descricao da sessao (opcional) */
  description?: string;
}

/**
 * Interface para dados de atualizacao de sessao
 * 
 * Define os dados opcionais para pausar ou retomar uma sessao.
 */
export interface UpdateSessionRequest {
  /** Descricao da sessao (opcional) */
  description?: string;
}

/**
 * Interface para dados de parada de sessao
 * 
 * Define os dados opcionais para parar uma sessao de trabalho.
 */
export interface StopSessionRequest {
  /** Descricao da sessao (opcional) */
  description?: string;
  /** Timestamp de fim personalizado (opcional) */
  endTime?: string;
}

/**
 * Interface para resposta de inicio de sessao
 * 
 * Contem os dados retornados ao iniciar uma nova sessao.
 */
export interface StartSessionResponse {
  /** Indica se a operacao foi bem-sucedida */
  success: boolean;
  /** Mensagem de retorno */
  message: string;
  /** Dados da sessao criada */
  session: WorkSession;
}

/**
 * Interface para resposta de pausa de sessao
 * 
 * Contem os dados retornados ao pausar uma sessao.
 */
export interface PauseSessionResponse {
  /** Indica se a operacao foi bem-sucedida */
  success: boolean;
  /** Mensagem de retorno */
  message: string;
  /** Dados da sessao pausada */
  session: WorkSession;
}

/**
 * Interface para resposta de retomada de sessao
 * 
 * Contem os dados retornados ao retomar uma sessao pausada.
 */
export interface ResumeSessionResponse {
  /** Indica se a operacao foi bem-sucedida */
  success: boolean;
  /** Mensagem de retorno */
  message: string;
  /** Dados da sessao retomada */
  session: WorkSession;
  /** Duracao da pausa em horas */
  pausedDuration: number;
}

/**
 * Interface para resposta de parada de sessao
 * 
 * Contem os dados retornados ao parar uma sessao.
 */
export interface StopSessionResponse {
  /** Indica se a operacao foi bem-sucedida */
  success: boolean;
  /** Mensagem de retorno */
  message: string;
  /** Dados da sessao finalizada */
  session: WorkSession;
  /** Duracao total da sessao em horas */
  duration: number;
}

/**
 * Interface para resposta de listagem de sessoes
 * 
 * Contem os dados das sessoes e informacoes de paginacao.
 */
export interface ListSessionsResponse {
  /** Indica se a operacao foi bem-sucedida */
  success: boolean;
  /** Array de sessoes retornadas */
  sessions: WorkSession[];
  /** Informacoes de paginacao */
  pagination: {
    /** Total de registros */
    total: number;
    /** Limite por pagina */
    limit: number;
    /** Offset atual */
    offset: number;
    /** Se ha mais paginas */
    hasMore: boolean;
  };
}

/**
 * Interface para resposta de listagem de sessoes ativas
 * 
 * Contem os dados das sessoes ativas do usuario.
 */
export interface ListActiveSessionsResponse {
  /** Indica se a operacao foi bem-sucedida */
  success: boolean;
  /** Array de sessoes ativas */
  sessions: WorkSession[];
  /** Contador de sessoes ativas */
  count: number;
}

/**
 * Interface para resposta de listagem de jobs recentes
 * 
 * Contem os dados dos ultimos jobs que o usuario iniciou.
 */
export interface ListRecentJobsResponse {
  /** Indica se a operacao foi bem-sucedida */
  success: boolean;
  /** Array de sessoes recentes */
  sessions: WorkSession[];
  /** Informacoes de paginacao */
  pagination: {
    /** Total de registros */
    total: number;
    /** Limite por pagina */
    limit: number;
    /** Offset atual */
    offset: number;
    /** Se ha mais paginas */
    hasMore: boolean;
  };
}

// ============================================================================
// FUNCOES DE API
// ============================================================================

// ============================================================================
// CRUD DE WORKSESSION
// ============================================================================

/**
 * Iniciar uma nova sessao de trabalho
 * 
 * Esta funcao inicia uma nova sessao de trabalho para uma OS especifica.
 * A sessao e criada com status ativo e timer iniciado.
 * 
 * Processo:
 * 1. Valida se a OS existe e pertence ao tenant
 * 2. Verifica se nao ha sessao ativa para a OS
 * 3. Cria nova sessao com timestamp de inicio
 * 4. Marca OS como ativa no sistema de heartbeat
 * 5. Retorna dados da sessao criada
 * 
 * @param osId ID da OS para iniciar sessao
 * @param data Dados opcionais da sessao (descricao)
 * @returns Promise com dados da sessao criada
 * @throws Error se OS nao encontrada ou ja tem sessao ativa
 */
export const startSession = async (
  osId: string,
  data?: StartSessionRequest
): Promise<StartSessionResponse> => {
  try {
    const response = await api.post(
      `/os/${osId}/sessions/start`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Erro ao iniciar sessão de trabalho'
    );
  }
};

/**
 * Pausar uma sessao de trabalho ativa
 * 
 * Esta funcao pausa uma sessao de trabalho ativa, interrompendo
 * o timer e registrando o timestamp de pausa.
 * 
 * Processo:
 * 1. Valida se a sessao existe e esta ativa
 * 2. Marca sessao como pausada
 * 3. Registra timestamp de pausa
 * 4. Calcula duracao ate o momento da pausa
 * 5. Retorna dados da sessao pausada
 * 
 * @param osId ID da OS da sessao
 * @param sessionId ID da sessao a ser pausada
 * @returns Promise com dados da sessao pausada
 * @throws Error se sessao nao encontrada ou ja pausada
 */
export const pauseSession = async (
  osId: string,
  sessionId: string
): Promise<PauseSessionResponse> => {
  try {
    const response = await api.put(
      `/os/${osId}/sessions/${sessionId}/pause`,
      {}
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Erro ao pausar sessão de trabalho'
    );
  }
};

/**
 * Retomar uma sessao de trabalho pausada
 * 
 * Esta funcao retoma uma sessao de trabalho pausada, reiniciando
 * o timer e registrando o timestamp de retomada.
 * 
 * Processo:
 * 1. Valida se a sessao existe e esta pausada
 * 2. Marca sessao como ativa novamente
 * 3. Registra timestamp de retomada
 * 4. Calcula duracao da pausa
 * 5. Retorna dados da sessao retomada
 * 
 * @param osId ID da OS da sessao
 * @param sessionId ID da sessao a ser retomada
 * @returns Promise com dados da sessao retomada
 * @throws Error se sessao nao encontrada ou nao esta pausada
 */
export const resumeSession = async (
  osId: string,
  sessionId: string
): Promise<ResumeSessionResponse> => {
  try {
    const response = await api.put(
      `/os/${osId}/sessions/${sessionId}/resume`,
      {}
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Erro ao retomar sessão de trabalho'
    );
  }
};

/**
 * Parar uma sessao de trabalho ativa
 * 
 * Esta funcao finaliza uma sessao de trabalho ativa, calculando
 * a duracao total e marcando como finalizada.
 * 
 * Processo:
 * 1. Valida se a sessao existe e esta ativa
 * 2. Marca sessao como finalizada
 * 3. Registra timestamp de fim
 * 4. Calcula duracao total da sessao
 * 5. Atualiza horas trabalhadas da OS
 * 6. Retorna dados da sessao finalizada
 * 
 * @param osId ID da OS da sessao
 * @param sessionId ID da sessao a ser parada
 * @param data Dados opcionais (descricao, endTime)
 * @returns Promise com dados da sessao finalizada
 * @throws Error se sessao nao encontrada ou ja finalizada
 */
export const stopSession = async (
  osId: string,
  sessionId: string,
  data?: StopSessionRequest
): Promise<StopSessionResponse> => {
  try {
    const response = await api.put(
      `/os/${osId}/sessions/${sessionId}/stop`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Erro ao parar sessão de trabalho'
    );
  }
};

/**
 * Listar todas as sessoes de uma OS
 * 
 * Esta funcao retorna uma lista paginada de sessoes de trabalho
 * para uma OS especifica, com filtros opcionais.
 * 
 * Filtros disponiveis:
 * - limit: Limite de registros por pagina
 * - offset: Offset para paginacao
 * - active: Filtrar apenas sessoes ativas
 * 
 * @param osId ID da OS para listar sessoes
 * @param params Parametros de filtro e paginacao (opcional)
 * @returns Promise com lista paginada de sessoes
 * @throws Error se OS nao encontrada ou sem permissao
 */
export const listSessions = async (
  osId: string,
  params?: {
    limit?: number;
    offset?: number;
    active?: boolean;
  }
): Promise<ListSessionsResponse> => {
  try {
    const response = await api.get(
      `/os/${osId}/sessions`,
      { params }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Erro ao listar sessões'
    );
  }
};

/**
 * Listar todas as sessoes ativas do usuario logado
 * 
 * Esta funcao retorna todas as sessoes de trabalho ativas
 * do usuario autenticado, permitindo gerenciar multiplas
 * atividades simultaneas.
 * 
 * Utilizada para:
 * - Dashboard de sessoes ativas
 * - Controle de multiplas atividades
 * - Monitoramento de tempo em tempo real
 * 
 * @returns Promise com lista de sessoes ativas
 * @throws Error se usuario nao autenticado
 */
export const listActiveSessions = async (): Promise<ListActiveSessionsResponse> => {
  try {
    const response = await api.get(
      `/sessions/active`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Erro ao listar sessões ativas'
    );
  }
};

/**
 * Listar jobs recentes do usuario logado
 * 
 * Esta funcao retorna os ultimos jobs que o usuario iniciou,
 * ordenados por data de inicio (mais recentes primeiro).
 * Inclui informacoes completas da OS, demanda e projeto.
 * 
 * Parametros opcionais:
 * - limit: Limite de registros por pagina (default: 20)
 * - offset: Offset para paginacao (default: 0)
 * 
 * @param params Parametros de paginacao (opcional)
 * @returns Promise com lista paginada de jobs recentes
 * @throws Error se usuario nao autenticado
 */
export const listRecentJobs = async (
  params?: {
    limit?: number;
    offset?: number;
    onlyMine?: boolean;
  }
): Promise<ListRecentJobsResponse> => {
  try {
    const queryParams: any = {};
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.offset) queryParams.offset = params.offset;
    if (params?.onlyMine !== undefined) queryParams.onlyMine = params.onlyMine.toString();
    const response = await api.get(
      `/sessions/recent`,
      { params: queryParams }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Erro ao listar jobs recentes'
    );
  }
};

// ============================================================================
// HELPERS E UTILITARIOS
// ============================================================================

/**
 * Calcular duracao em tempo real de uma sessao ativa
 * 
 * Esta funcao calcula a duracao atual de uma sessao de trabalho
 * considerando pausas e tempo decorrido em tempo real.
 * 
 * Logica de calculo:
 * 1. Se sessao nao esta ativa, retorna duracao salva
 * 2. Calcula tempo total decorrido desde o inicio
 * 3. Subtrai tempo total de pausas ja registradas
 * 4. Subtrai tempo de pausa atual (se pausada)
 * 5. Retorna duracao real em horas
 * 
 * @param session Sessao de trabalho para calcular duracao
 * @returns Duracao em horas (decimal)
 */
export const calculateRealTimeDuration = (session: WorkSession): number => {
  if (!session.isActive) {
    return session.duration;
  }

  const now = new Date();
  const startTime = new Date(session.startTime);
  const totalMs = now.getTime() - startTime.getTime();
  const totalHours = totalMs / (1000 * 60 * 60);

  // Calcular tempo de pausa atual se estiver pausada
  let currentPauseTime = 0;
  if (session.isPaused && session.pausedAt) {
    const pausedAt = new Date(session.pausedAt);
    const pauseMs = now.getTime() - pausedAt.getTime();
    currentPauseTime = pauseMs / (1000 * 60 * 60);
  }

  // Duracao total = tempo decorrido - tempo total pausado - tempo de pausa atual
  const realDuration = totalHours - session.totalPausedTime - currentPauseTime;
  
  // Garantir que nao seja negativo
  return Math.max(0, realDuration);
};

/**
 * Formatar duracao em horas para HH:MM:SS
 * 
 * Esta funcao converte horas decimais para formato de tempo
 * padronizado (HH:MM:SS) para exibicao em timers.
 * 
 * @param hours Duracao em horas (decimal)
 * @returns String formatada no formato HH:MM:SS
 */
export const formatDuration = (hours: number): string => {
  const totalSeconds = Math.floor(hours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;

  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

/**
 * Formatar duracao em horas para formato legivel
 * 
 * Esta funcao converte horas decimais para formato legivel
 * (ex: "2h 30min") para exibicao em interfaces.
 * 
 * @param hours Duracao em horas (decimal)
 * @returns String formatada no formato legivel
 */
export const formatDurationReadable = (hours: number): string => {
  const h = Math.floor(hours);
  const m = Math.floor((hours - h) * 60);

  if (h === 0 && m === 0) {
    return '0min';
  }
  if (h === 0) {
    return `${m}min`;
  }
  if (m === 0) {
    return `${h}h`;
  }
  return `${h}h ${m}min`;
};

/**
 * Verificar se uma sessao esta ativa
 * 
 * Esta funcao verifica se uma sessao de trabalho esta ativa
 * (executando e nao pausada).
 * 
 * @param session Sessao de trabalho para verificar
 * @returns true se a sessao esta ativa
 */
export const isSessionActive = (session: WorkSession): boolean => {
  return session.isActive && !session.isPaused;
};

/**
 * Verificar se uma sessao esta pausada
 * 
 * Esta funcao verifica se uma sessao de trabalho esta pausada
 * (ativa mas pausada).
 * 
 * @param session Sessao de trabalho para verificar
 * @returns true se a sessao esta pausada
 */
export const isSessionPaused = (session: WorkSession): boolean => {
  return session.isActive && session.isPaused;
};

/**
 * Verificar se uma sessao esta finalizada
 * 
 * Esta funcao verifica se uma sessao de trabalho esta finalizada
 * (nao ativa e com timestamp de fim).
 * 
 * @param session Sessao de trabalho para verificar
 * @returns true se a sessao esta finalizada
 */
export const isSessionFinished = (session: WorkSession): boolean => {
  return !session.isActive && session.endTime !== null;
};

/**
 * Obter status da sessao (ativa, pausada, finalizada)
 * 
 * Esta funcao retorna o status atual de uma sessao de trabalho
 * baseado nas propriedades isActive, isPaused e endTime.
 * 
 * @param session Sessao de trabalho para verificar
 * @returns Status da sessao ('active', 'paused', 'finished')
 */
export const getSessionStatus = (session: WorkSession): 'active' | 'paused' | 'finished' => {
  if (isSessionFinished(session)) return 'finished';
  if (isSessionPaused(session)) return 'paused';
  return 'active';
};

/**
 * Obter cor do status da sessao
 * 
 * Esta funcao retorna a cor correspondente ao status da sessao
 * para exibicao em badges e indicadores visuais.
 * 
 * @param session Sessao de trabalho para verificar
 * @returns Cor do status (green, yellow, gray)
 */
export const getSessionStatusColor = (session: WorkSession): string => {
  const status = getSessionStatus(session);
  switch (status) {
    case 'active':
      return 'green';
    case 'paused':
      return 'yellow';
    case 'finished':
      return 'gray';
    default:
      return 'gray';
  }
};

/**
 * Obter label do status da sessao
 * 
 * Esta funcao retorna o label em portugues correspondente
 * ao status da sessao para exibicao na interface.
 * 
 * @param session Sessao de trabalho para verificar
 * @returns Label do status em portugues
 */
export const getSessionStatusLabel = (session: WorkSession): string => {
  const status = getSessionStatus(session);
  switch (status) {
    case 'active':
      return 'Em Andamento';
    case 'paused':
      return 'Pausada';
    case 'finished':
      return 'Finalizada';
    default:
      return 'Desconhecido';
  }
};

// ============================================================================
// REACT QUERY HOOKS (Para uso nos componentes)
// ============================================================================

/**
 * Keys para React Query
 * 
 * Define as chaves de cache para React Query, permitindo
 * invalidação seletiva e gerenciamento de cache eficiente.
 * 
 * Estrutura hierárquica:
 * - all: Chave base para todas as work sessions
 * - lists: Chave para listagens
 * - list: Chave para lista específica com filtros
 * - active: Chave para sessoes ativas
 * - detail: Chave para detalhes de sessao específica
 */
export const workSessionKeys = {
  /** Chave base para todas as work sessions */
  all: ['workSessions'] as const,
  /** Chave para listagens de work sessions */
  lists: () => [...workSessionKeys.all, 'list'] as const,
  /** Chave para lista específica com filtros */
  list: (osId: string, filters?: any) => [...workSessionKeys.lists(), osId, filters] as const,
  /** Chave para sessoes ativas */
  active: () => [...workSessionKeys.all, 'active'] as const,
  /** Chave para detalhes de sessao específica */
  detail: (osId: string, sessionId: string) => [...workSessionKeys.all, 'detail', osId, sessionId] as const,
};

export default {
  startSession,
  pauseSession,
  resumeSession,
  stopSession,
  listSessions,
  listActiveSessions,
  listRecentJobs,
  calculateRealTimeDuration,
  formatDuration,
  formatDurationReadable,
  isSessionActive,
  isSessionPaused,
  isSessionFinished,
  getSessionStatus,
  getSessionStatusColor,
  getSessionStatusLabel,
  workSessionKeys,
};

